/**
 * EventBanner – zeigt aktive Spiel-Events prominent oben in jeder Spieler-Seite.
 */
import { Component, createSignal, onMount, onCleanup, For, Show } from 'solid-js';
import apiClient from '../utils/api';

interface GameEvent {
  id: number;
  name: string;
  description: string | null;
  event_type: string;
  multiplier: number;
  bonus_flat: number;
  starts_at: string;
  ends_at: string;
}

const TYPE_ICON: Record<string, string> = {
  bonus_money:         '💰',
  bonus_points:        '⭐',
  double_food_yield:   '🍯',
  faster_breeding:     '🐛',
  faster_research:     '🔬',
  job_bonus:           '💼',
  reduced_consumption: '🌿',
  queen_search_boost:  '👑',
  custom:              '🎉',
};

const TYPE_LABEL: Record<string, string> = {
  bonus_money:         'Bonus-Geld',
  bonus_points:        'Bonus-Punkte',
  double_food_yield:   'Doppelte Futterausbeute',
  faster_breeding:     'Schnellere Zucht',
  faster_research:     'Forschungs-Boost',
  job_bonus:           'Job-Bonus',
  reduced_consumption: 'Reduzierter Verbrauch',
  queen_search_boost:  'Königin-Suche Boost',
  custom:              'Sonderevent',
};

// Farb-Thema je Event-Typ
const TYPE_THEME: Record<string, { from: string; to: string; glow: string; badge: string }> = {
  bonus_money:         { from: '#f59e0b', to: '#d97706', glow: 'rgba(245,158,11,0.4)',  badge: 'rgba(245,158,11,0.25)' },
  bonus_points:        { from: '#a78bfa', to: '#7c3aed', glow: 'rgba(167,139,250,0.4)', badge: 'rgba(167,139,250,0.25)' },
  double_food_yield:   { from: '#fb923c', to: '#ea580c', glow: 'rgba(251,146,60,0.4)',  badge: 'rgba(251,146,60,0.25)' },
  faster_breeding:     { from: '#34d399', to: '#059669', glow: 'rgba(52,211,153,0.4)',  badge: 'rgba(52,211,153,0.25)' },
  faster_research:     { from: '#38bdf8', to: '#0284c7', glow: 'rgba(56,189,248,0.4)',  badge: 'rgba(56,189,248,0.25)' },
  job_bonus:           { from: '#fbbf24', to: '#b45309', glow: 'rgba(251,191,36,0.4)',  badge: 'rgba(251,191,36,0.25)' },
  reduced_consumption: { from: '#86efac', to: '#16a34a', glow: 'rgba(134,239,172,0.4)', badge: 'rgba(134,239,172,0.25)' },
  queen_search_boost:  { from: '#f9a8d4', to: '#db2777', glow: 'rgba(249,168,212,0.4)', badge: 'rgba(249,168,212,0.25)' },
  custom:              { from: '#e879f9', to: '#a21caf', glow: 'rgba(232,121,249,0.4)', badge: 'rgba(232,121,249,0.25)' },
};
const DEFAULT_THEME = { from: '#f59e0b', to: '#d97706', glow: 'rgba(245,158,11,0.4)', badge: 'rgba(245,158,11,0.2)' };

function fmtRemaining(endsAt: string): string {
  const ms = new Date(endsAt).getTime() - Date.now();
  if (ms <= 0) return 'endet gleich';
  const totalSec = Math.floor(ms / 1000);
  const d = Math.floor(totalSec / 86400);
  const h = Math.floor((totalSec % 86400) / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  if (d > 0) return `${d}T ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

const DISMISSED_KEY = 'dismissed_events_v1';
function getDismissed(): Set<number> {
  try { return new Set(JSON.parse(sessionStorage.getItem(DISMISSED_KEY) || '[]')); } catch { return new Set(); }
}
function dismiss(id: number) {
  try { const s = getDismissed(); s.add(id); sessionStorage.setItem(DISMISSED_KEY, JSON.stringify([...s])); } catch {}
}

const EventBanner: Component = () => {
  const [events, setEvents] = createSignal<GameEvent[]>([]);
  const [dismissed, setDismissed] = createSignal<Set<number>>(getDismissed());
  const [tick, setTick] = createSignal(0);

  const [claimMsg, setClaimMsg] = createSignal('');

  const claim = async () => {
    try {
      const res = await apiClient.post<{ success: boolean; data: Array<{ name: string; type: string; amount: number }> }>('/game-events/claim', {});
      const got = res.data?.data || [];
      if (got.length) {
        setClaimMsg(got.map(c => `🎁 ${c.name}: +${c.amount} ${c.type === 'bonus_money' ? '€' : 'Punkte'} gutgeschrieben`).join(' · '));
        setTimeout(() => setClaimMsg(''), 12000);
      }
    } catch {}
  };

  const load = async () => {
    try {
      const res = await apiClient.get<{ success: boolean; data: GameEvent[] }>('/game-events/active');
      if (res.data?.success) {
        const list = (res.data.data || []).filter(e => e.event_type !== 'seasonal_effect');
        setEvents(list);
        if (list.some(e => e.event_type === 'bonus_money' || e.event_type === 'bonus_points')) claim();
      }
    } catch {}
  };

  onMount(() => {
    load();
    const poll = setInterval(load, 5 * 60 * 1000);
    const clock = setInterval(() => setTick(t => t + 1), 30000);
    onCleanup(() => { clearInterval(poll); clearInterval(clock); });
  });

  const visible = () => { tick(); return events().filter(e => !dismissed().has(e.id)); };

  const close = (id: number) => { dismiss(id); setDismissed(new Set(getDismissed())); };

  return (
    <Show when={visible().length > 0}>
      <div class="evb-stack">
        <Show when={claimMsg()}>
          <div style="padding:0.5rem 0.875rem;border-radius:0.5rem;background:rgba(34,197,94,0.15);border:1px solid rgba(34,197,94,0.35);color:#4ade80;font-size:0.85rem;font-weight:600;">{claimMsg()}</div>
        </Show>
        <For each={visible()}>
          {(ev) => {
            const theme = TYPE_THEME[ev.event_type] || DEFAULT_THEME;
            const hasBonus = Number(ev.multiplier) !== 1 || Number(ev.bonus_flat) > 0;
            return (
              <div
                class="evb"
                style={`--evb-from:${theme.from};--evb-to:${theme.to};--evb-glow:${theme.glow};--evb-badge:${theme.badge};`}
              >
                {/* shimmer sweep */}
                <div class="evb-shimmer" />

                {/* left: event label pill */}
                <div class="evb-left">
                  <div class="evb-label-wrap">
                    <span class="evb-pulse" />
                    <span class="evb-label">🎪 EVENT</span>
                  </div>
                </div>

                {/* center: icon + name + desc */}
                <div class="evb-center">
                  <span class="evb-icon">{TYPE_ICON[ev.event_type] || '🎉'}</span>
                  <div class="evb-text">
                    <span class="evb-type">{TYPE_LABEL[ev.event_type] || 'Event'}</span>
                    <span class="evb-name">{ev.name}</span>
                    <Show when={ev.description}>
                      <span class="evb-desc">{ev.description}</span>
                    </Show>
                  </div>
                  <Show when={hasBonus}>
                    <div class="evb-bonus">
                      <Show when={Number(ev.multiplier) !== 1}>
                        <span class="evb-bonus-val">×{ev.multiplier}</span>
                      </Show>
                      <Show when={Number(ev.bonus_flat) > 0}>
                        <span class="evb-bonus-val">+{ev.bonus_flat}</span>
                      </Show>
                    </div>
                  </Show>
                </div>

                {/* right: countdown + close */}
                <div class="evb-right">
                  <div class="evb-countdown">
                    <span class="evb-countdown-label">Endet in</span>
                    <span class="evb-countdown-val">{fmtRemaining(ev.ends_at)}</span>
                  </div>
                  <button class="evb-close" onClick={() => close(ev.id)} title="Ausblenden">✕</button>
                </div>
              </div>
            );
          }}
        </For>
      </div>
    </Show>
  );
};

export default EventBanner;
